import { useCallback, useEffect, useRef, useState } from 'react';
import { useDependency } from '../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import { CLOSED_TITLES, normalizeText, statusColor, toBase64Unicode } from '../../../utils/helperUtil';
import useBrowserName from '../../../hooks/UseBrowserName';
import useScreenSize from '../../../hooks/UseScreenSize';
import { ToastQueue } from '@react-spectrum/toast';
import {
    Flex,
    View,
    ProgressCircle,
    Grid,
    Heading,
    Content,
    ListBox,
    Item,
    IllustratedMessage,
    Link,
    DialogContainer,
    TableView,
    TableHeader,
    TableBody,
    Row,
    Column,
    Cell,
    useAsyncList,
} from '@adobe/react-spectrum';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import FilterComponent from './components/FilterComponent';
import HeaderComponent from './components/HeaderComponent';
import { LayoutDefinition } from '../../../infra/protected/Layout/LayoutDefinitions';
import CardComponent from './components/CardComponent';
import { RootState } from '../../../state/store';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { updateAppInfo } from '../../../state/userWebLayout/userWebLayoutSlice';
import styles from './Task.module.css';
import { DeleteConfirmationDialog } from '../finder/components/DeleteConfirmationDialog';
import { GetSchedulerSettingsRequest, SettingsItemType } from '../../../services/soap/scheduler/requests/GetSchedulerSettingsRequest';
import { CircleFill } from 'react-bootstrap-icons';
import moment from 'moment';
import { ListTaskRequest } from '../../../services/soap/scheduler/requests/ListTaskRequest';
import { ListUserRequest } from '../../../services/soap/access/requests/ListUserRequest';
import FilterSearchTextBox from './components/FilterSearchTextBox';
import { GetTaskRequest } from '../../../services/soap/scheduler/requests/GetTaskRequest';
import TaskDetailsView from './components/TaskDetailsView';
import { UpdateTaskRequest } from '../../../services/soap/scheduler/requests/UpdateTaskRequest';
import { TaskItem } from '../../../services/soap/dataObjects/TaskItem';
import TaskDetailsViewMobile from './components/TaskDetailsViewMobile';
import { useDeviceDetection } from '../../../hooks/UseDeviceDetection';
import CardComponentMobile from './components/CardComponentMobile';
import AddUpdateTask from './components/AddUpdateTask';
import { DeleteTaskRequest } from '../../../services/soap/scheduler/requests/DeleteTaskRequest';
import { addKey, useKeyboardShortcuts } from '../../../hooks/UseKeyboardShortcuts';
import { getTypeOfWorkFromLogin, TypeOfWorkBinding } from '../../../utils/LoginUtils';
interface Character {
    id: string;
    title: string;
}

export function Task() {
    const browserName = useBrowserName();
    const screenSize = useScreenSize();
    const { t } = useTranslation('task');
    const _t = (key: string) => t(`task_detail.${key}`, { ns: 'task' });
    const _tt = (key: string) => t(`task_common.${key}`, { ns: 'task' });
    const _ttt = (key: string) => t(`task_table_column.${key}`, { ns: 'task' });
    const _m = (key: string) => t(`task_messages.${key}`, { ns: 'task' });
    const { isMobile } = useViewInfo();
    const { store, schedulerService, itemService, accessService } = useDependency();
    const dataStore = useSelector((state: any) => state.userWebLayout.appInfos);
    const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
    const dispatch = useDispatch();
    const { state } = useLocation();
    const [showLoader, setShowLoader] = useState(true);
    const [showLoaderDetail, setShowLoaderDetail] = useState(true);
    const [rowsData, setRowsData] = useState<any>([]);
    const [columnsData, setColumnsData] = useState<any>([]);
    const [pageView, setPageView] = useState<string>('Full');
    const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
    const [taskStatusList, setTaskStatusList] = useState<any>([]);
    const [definition, setDefinition] = useState<LayoutDefinition | null>(null);
    const [showMobileDetailView, setShowMobileDetailView] = useState<boolean>(false);
    const [stopLoadData, setStopLoadData] = useState<boolean>(false);
    const [searchedValue, setSearchedValue] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<any | null>(null);
    const [selectedDataRow, setSelectedDataRow] = useState<any>({});
    const serviceActivityValue = useRef<string>('');
    const queryParams = useRef<any>({ FILTER: { COL: [] } });
    const totalProductListRec = useRef<number>(0);
    const offSetLimit = useRef<number>(0);
    const dataRef = useRef<any>({});
    const [showAdd, setShowAdd] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [params] = useSearchParams();
    const [showCards, setShowCards] = useState<boolean>(true);
    const [isOpen, setOpen] = useState(false);
    const [userData, setUserData] = useState<any>([]);
    const [typeofWorkList, setTypeofWorkList] = useState<any>([]);
    const isInitializingRef = useRef<boolean>(false);
    const deviceInfo = useDeviceDetection(isMobile);
    const [showFilter, setshowFilter] = useState<boolean>(false);
    const [showUpdateDescLoader, setShowUpdateDescLoader] = useState<boolean>(false);
    const PAGE_SIZE = 100;
    useEffect(() => {
        if (state.appKey && dataStore[state.appKey]) {
            if (dataStore[state.appKey].pageView) {
                dataRef.current.pageView = dataStore[state.appKey].pageView;
                setPageView(dataStore[state.appKey].pageView);
                return;
            }
        }
    }, [dataStore.appInfos, state.appKey]);

    useEffect(() => {
        (async () => {
            if (params.has('id')) {
                setShowCards(false);
            }
            setShowLoader(true);
            setShowLoaderDetail(true);
            queryParams.current = { FILTER: { COL: [] } };
            serviceActivityValue.current = '';
            setPageView('Full');
            setSearchedValue(moment(new Date()).format('YYYY-MM-DD'));
            Promise.all([getTaskStatusList()]);
        })();
    }, []);

    useEffect(() => {
        if (selectedApp !== '') {
            listTaskList.reload();
        }
    }, [selectedApp]);

    useEffect(() => {
        return () => {
            dispatch(
                updateAppInfo({
                    key: state.appKey,
                    data: {
                        pageView: dataRef.current.pageView,
                    },
                })
            );
        };

    }, []);

    const getFormSettings = useCallback(
        async (settingId: string) => {
            setColumnsData([
                { name: _ttt('type'), key: 'entryTypeName', width: 170 },
                { name: _ttt('title'), key: 'title', width: 400 },
                { name: _ttt('priority'), key: 'priority', width: 100 },
                { name: _ttt('task_owner'), key: 'userName', width: 200 },
                { name: _ttt('deadline'), key: 'deadLine', width: 200 },
                { name: _ttt('status'), key: 'statusName', width: 200 },
            ]);
        },
        [selectedApp]
    );

    const getTaskStatusList = useCallback(async () => {
        const statusArray: any = [];
        let res: any = await schedulerService.getSchedulerSettings(new GetSchedulerSettingsRequest(store.Server, store.SessionId,
            '1', SettingsItemType.TASK_ITEM_TYPE, 50, 0));
        if (res.result === 'OK' && res.ITEMS.length > 0) {

            res.ITEMS.sort((a: any, b: any) => {
                return parseInt(a.ITEM.fixedStatus) - parseInt(b.ITEM.fixedStatus);
            });

            res?.ITEMS.forEach((status: any) => {
                const { ident, title, color } = status.ITEM;
                let colorString = statusColor(color);
                statusArray.push({ ident, title, color: colorString });
            });

            statusArray.unshift({ ident: 'All states', title: '', color: 'black' });
            setTaskStatusList(statusArray);
            console.log('taskStatusList', res,statusArray)
            return statusArray;
        }
    }, [selectedApp]);

    const addDays = (d: Date, days: number) => {
        const x = new Date(d);
        x.setDate(x.getDate() + days);
        return x;
    };

    const getStatusColor = (statusArray: any, solutionStatus: any) => {
        const statusObj = statusArray.find((obj: any) => obj.ident == solutionStatus);
        return (
            <>
                <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={statusObj?.color} /> {statusObj?.title}
            </>
        );
    };

    const getPriorityLabel = (id: number) => {
        const priority = getPriority(id)
        if (!priority) return (<></>)

        return (
            <Flex direction={'row'} UNSAFE_style={{ backgroundColor: priority && priority.color }} alignItems={'center'} justifyContent={'center'} UNSAFE_className={styles.footer_wrapper}>
                <Flex direction={'row'} alignItems={'center'} justifyContent={'center'} gap={'6px'}>
                    <View position={'relative'} UNSAFE_className={styles.footer_text_1}>
                        <span title={priority.name}>{priority && priority.name}</span>
                    </View>
                </Flex>
            </Flex>
        )
    }

    const getStatusBackGroundColor = (solutionStatus: string, statusArray?: any) => {
        const status = statusArray.find((x: any) => x.ident === solutionStatus)
        return status?.color || ''
    };

    const getUserCount = async () => {
        let userList = await accessService.listUser(new ListUserRequest(store.Server, store.SessionId, true, undefined, undefined, '1'));
        if (userList.result === 'OK') {
            return userList.count;
        }
    };

    const getUserData = async (totalRecords: any) => {
        let userResp = await accessService.listUser(new ListUserRequest(store.Server, store.SessionId, true, totalRecords));
        if (userResp.result === 'OK') {
            const formattedArray = userResp.USERS.map(({ USER }) => ({
                id: USER.id,
                name: `${USER.name1} ${USER.name3}`,
            }));
            setUserData(formattedArray);

            return formattedArray;
        }
    }

    const getUserName = (userId: any, userArray?: any) => {
        if (userArray.length > 0) {
            const userResp = userArray.find((x: any) => x.id === userId)
            return userResp?.name || '';
        } else {
            const userResp = userData.find((x: any) => x.id === userId)
            return userResp?.name || '';
        }
    };

    const getTypeofWorkList = async () => {
        const bindedTypeOfWork = getTypeOfWorkFromLogin(TypeOfWorkBinding.Task, store.session.organization);
        console.log('bindedTypeOfWork', bindedTypeOfWork)
        const statusArray: any = []
        bindedTypeOfWork?.forEach((status: any) => {
            const { ident, title, color, defaultFor } = status;
            let colorString = statusColor(color);
            statusArray.push({ ident, title, color: colorString, defaultFor });
        });
        setTypeofWorkList(statusArray);
        return statusArray
    };


    const getTaskListSearchParams = () => {
        // const end = addDays(new Date(), -30);
        const now = new Date();
        // 1st of current month at 00:00 local time
        const rangeStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        // 1st of next month at 00:00 local time
        const rangeEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);

        const taskListSearchParams: any = {
            //rangeStart: new Date(moment(new Date()).format('YYYY-MM-DDTHH:mm:ss')),
            //rangeEnd: new Date(moment(new Date(end)).format('YYYY-MM-DDTHH:mm:ss')),
            //rangeStart: new Date('2025-11-01'),
            //rangeEnd: new Date('2025-12-01'),
            rangeStart: rangeStart,
            rangeEnd: rangeEnd,
            userId: String(store.UserId),
            anyTime: '0',
            includePast: '1',
            userFilter: '1',
            referenceId: '',
            countOnly: '',
            dateOfInterest: '1',
            groupUsers: '1',
            USERFILTER: [{ id: store.UserId }],
        };

        return taskListSearchParams;
    };

    const mapFiltersToRequest = (filters: { COL: any[] }) => {
        const requestParams: {
            countOnly?: boolean;
            entryType?: string;
            status?: string;
            refId?: string;
            manufacturer?: string;
            date?: string;
            taskView?: string;
            users?: Array<{ id: string }>;
            standardStatus?: string;
        } = {
        };

        filters.COL.forEach(filter => {
            if (filter.name === 'contains') {
                requestParams.date = `${filter.value}`;
            }
            if (filter.name === 'user') {
                const raw = filter.value;
                const first =
                    raw instanceof Set ? Array.from(raw)[0]
                        : Array.isArray(raw) ? raw[0]
                            : raw;

                if (first && String(first).trim().toLowerCase() === 'all users') {
                    return;
                }
                const ids =
                    raw instanceof Set
                        ? Array.from(raw)
                        : Array.isArray(raw)
                            ? raw
                            : typeof raw === "string"
                                ? raw.split(",")
                                : raw != null
                                    ? [raw]
                                    : [];

                requestParams.users = ids
                    .map((id) => String(id).trim())
                    .filter(Boolean)
                    .map((id) => ({ id }));
            }
            if (filter.name === 'manufacturer') {
                requestParams.manufacturer = filter.value;
            }
            if (filter.name === 'status') {
                requestParams.status = filter.value;
            }
            if (filter.name === 'view') {
                requestParams.taskView = filter.value;
            }
            if (filter.name === 'customState') {
                const raw = filter.value;
                const first =
                    raw instanceof Set ? Array.from(raw)[0]
                        : Array.isArray(raw) ? raw[0]
                            : raw;

                if (first && String(first).trim().toLowerCase() === 'all states') {
                    return;
                }
                requestParams.entryType = first;
            }
            if (filter.name === 'standardStatus') {
                const raw = filter.value;
                const first =
                    raw instanceof Set ? Array.from(raw)[0]
                        : Array.isArray(raw) ? raw[0]
                            : raw;

                if (first && String(first).trim().toLowerCase() === '0') {
                    return;
                }
                requestParams.standardStatus = first;
            }
        });
        return requestParams;
    };



    const getTaskListCount = async () => {
        const taskListSearchParams: any = getTaskListSearchParams();
        const requestParams = mapFiltersToRequest(queryParams.current.FILTER);

        // if (requestParams.taskView === 'task_to_check' || requestParams.taskView === 'custom_tasks') {
        //     taskListSearchParams.standardStatus = '1'
        // }

        if (requestParams.taskView === 'task_to_check') {
            const uf = taskListSearchParams.USERFILTER;
            const ids =
                Array.isArray(uf) ? uf.map(u => String(u.id)) :
                    uf?.id ? [String(uf.id)] :
                        [String(store.UserId)];

            taskListSearchParams.USERFILTER = ids.flatMap(id => ([
                { id },
                { id, asGarant: '1' },
                { id, asCreator: '1' }
            ]));
        }

        if (!requestParams.standardStatus) {
            requestParams.standardStatus = '1'
        }

        const params = { ...taskListSearchParams, ...requestParams };

        let taskListResp = await schedulerService.listTask(
            new ListTaskRequest(store.Server, store.SessionId, {
                ...params,
                countOnly: '1'
            })
        );
        if (taskListResp.result === 'OK') {
            totalProductListRec.current = (taskListResp.count != 0 ? taskListResp.count : 0);
            return taskListResp.count != 0 ? taskListResp.count : 0;
        }
    };

    const priorities = [
        { id: -2, name: _tt('highest_priority'), color: '#F72B1C' },
        { id: -1, name: _tt('high_priority'), color: '#FF9800' },
        { id: 0, name: _tt('normal_priority'), color: '#8BC34A' },
        { id: 1, name: _tt('low_priority'), color: '#14AFF5' },
        { id: 2, name: _tt('lowest_priority'), color: '#8F9FA6' }
    ];

    const getPriority = (id: number) => {
        const priority = priorities.find(p => p.id === id);
        return priority;
    }

    const loadTaskData: any = async (statusArray: any, userArray: any) => {
        const taskStatusArray = await getTaskStatusList();
        const typeOfWorkArray = await getTypeofWorkList();
        const taskListSearchParams: any = getTaskListSearchParams();
        const requestParams = mapFiltersToRequest(queryParams.current.FILTER);

        // if (requestParams.taskView === 'task_to_check' || requestParams.taskView === 'custom_tasks') {
        //     taskListSearchParams.standardStatus = '1'
        // }

        if (requestParams.taskView === 'task_to_check') {
            const uf = taskListSearchParams.USERFILTER;
            const ids =
                Array.isArray(uf) ? uf.map(u => String(u.id)) :
                    uf?.id ? [String(uf.id)] :
                        [String(store.UserId)];

            taskListSearchParams.USERFILTER = ids.flatMap(id => ([
                { id },
                { id, asGarant: '1' },
                { id, asCreator: '1' }
            ]));
        }

        if (!requestParams.standardStatus) {
            requestParams.standardStatus = '1'
        }

        const params = { ...taskListSearchParams, ...requestParams };
        let taskListResp = await schedulerService.listTask(
            new ListTaskRequest(store.Server, store.SessionId, {
                ...params,
                limit: PAGE_SIZE,
                offset: offSetLimit.current,
                countOnly: undefined,
            })
        );

        if (taskListResp.result === 'OK') {
            if (taskListResp.count.toString() === "0") {
                setRowsData([])
                return []
            }

            let taskListData = taskListResp.ITEMS.map((item: any) => ({
                id: item.ITEM.ident,
                userId: item.ITEM.userId,
                userName: getUserName(item.ITEM.userId, userArray),
                status: item.ITEM.status,
                statusName: getStatusColor(taskStatusArray, item.ITEM.solutionStatus),
                solutionStatus: item.ITEM.solutionStatus,
                deadLine: moment(item.ITEM.dateTo).format('MM/DD/YYYY | hh:mm A'),
                title: item.ITEM.title,
                completed: item.ITEM.completed,
                entryType: item.ITEM.entryType,
                entryTypeName: getStatusColor(typeOfWorkArray, item.ITEM.entryType),
                projectName: item.ITEM?.projectName || '',
                folderName: item.ITEM?.folderName || '',
                headId: item.ITEM.headId,
                statusColor: getStatusBackGroundColor(item.ITEM.entryType, statusArray),
                priority: getPriorityLabel(parseInt(item.ITEM?.priority))
            }));
            setShowLoaderDetail(false);
            return taskListData
        }

        return []
    }

    const loadTaskList = async (): Promise<any[]> => {
        try {
            if (stopLoadData) return rowsData;
            const statusArray = await getTypeofWorkList();
            const userArray = await getUserData(500);
            const [tasks] = await Promise.all([
                loadTaskData(statusArray, userArray)
            ]);

            const sortedData = [
                ...tasks,
            ];
            setRowsData(tasks);
            if (params.has('id')) {
                getLayout(+params.get('id')!, undefined, statusArray, userArray);
            } else {
                if (rowsData.length === 0) {
                    getLayout(sortedData[0]?.id, sortedData[0], statusArray, userArray);
                }
            }

            return sortedData;
        } finally {
            setShowLoader(false);
            setshowFilter(true);
        }
    };

    const listTaskList = useAsyncList<Character>({
        async load({ cursor, signal }) {
            try {
                if (isInitializingRef.current) {
                    return { items: [], cursor: undefined };
                }

                // if (!selectedApp) {
                //     return { items: [], cursor: undefined };
                // }

                const currentPage = cursor ? parseInt(cursor, 10) : 0;
                const estimatedLoadedCount = currentPage * PAGE_SIZE;
                if (cursor && estimatedLoadedCount >= totalProductListRec.current) {
                    return { items: rowsData, cursor: undefined };
                }

                const totalRecords = currentPage === 0 ? await getTaskListCount() : totalProductListRec.current;

                if (signal?.aborted) {
                    return { items: [], cursor: undefined };
                }

                await getFormSettings(dataRef.current.settingId);

                const offset = Math.min(PAGE_SIZE * currentPage, totalRecords ?? 0);

                offSetLimit.current = offset;

                const items = await loadTaskList();

                const hasMoreItems = offset + (items?.length || 0) < (totalRecords ?? 0);
                const nextCursor = hasMoreItems ? String(currentPage + 1) : undefined;

                return {
                    items: items || [],
                    cursor: nextCursor,
                };
            } catch (error) {
                console.error('Error loading activity list:', error);
                ToastQueue.negative(t('error_loading_data', { ns: 'layout_components' }) || 'Error loading data', { timeout: 3000 });
                return { items: [], cursor: undefined };
            }
        },
        async sort({ items, sortDescriptor }) {
            if (!sortDescriptor) return { items };

            const { column, direction } = sortDescriptor;

            return {
                items: [...items].sort((a: any, b: any) => {
                    const aValue = a[column];
                    const bValue = b[column];
                    let result = 0;
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        result = aValue.localeCompare(bValue);
                    } else if (aValue < bValue) {
                        result = -1;
                    } else if (aValue > bValue) {
                        result = 1;
                    }

                    return direction === 'descending' ? -result : result;
                }),
            };
        },

        getKey: item => item.id,
    });

    const getLayout = async (selectedId: any, selectedRow?: any, statusArray?: any, userArray?: any) => {
        try {
            setShowLoaderDetail(true);
            const taskResult = await schedulerService.getTask(
                new GetTaskRequest(
                    store.Server,
                    store.SessionId,
                    selectedId.toString(),
                    store.UserId.toString()
                )
            );
            if (taskResult.result !== 'OK') return;
            console.log('taskResult', taskResult);
            const items = normalizeEntries(taskResult.ITEM);
            const single = items[0] ?? null;

            const taskDetails = rowsData.find((x: any) => x.id === single.ident) || selectedRow;
            const statusItem = getClosedStatusIdent(taskStatusList);//taskStatusList.find((item: any) => item.title === 'Uzavřen');
            const statusId = statusItem ? statusItem.ident : '';

            const enterytypeItem = statusArray
                ? statusArray.find((item: any) => item.ident === single.entryType)
                : typeofWorkList.find((item: any) => item.ident === single.entryType);

            const userName = getUserName(single.userId, []);

            const result = single && single.ident
                ? {
                    id: single.ident,
                    ident: single.ident,
                    title: single.title,
                    entryTypeName: enterytypeItem?.title || '',
                    entryType: single.entryType,
                    solutionStatus: single.solutionStatus,
                    status: single.solutionStatus,
                    statusName: statusItem ? statusItem.title : '',
                    statusId: statusItem ? statusItem.ident : '',
                    solver: userName || '',
                    solverId: single.userId,
                    statusColor: taskDetails?.statusColor || '',
                    activityId: single.activityId,
                    activityName: single.activityName,
                    projectId: single.projectId,
                    projectName: single.projectName,
                    folderId: single.folderId,
                    folderName: single.folderName,
                    location: single.location,
                    description: single.comments,
                    headId: single.headId,
                    repeat: single.REPEAT,
                    dateFrom: fmtDate(single.dateFrom),
                    dateTo: fmtDate(single.dateTo),
                    deadLine: moment(single.dateTo).format('MM/DD/YYYY | hh:mm A'),
                    timeFrom: fmtTime(single.dateFrom),
                    timeTo: fmtTime(single.dateTo),
                    isChargeable: single?.chargeable === '1',
                    isPrivate: single?.isPrivate === '1',
                    isRepeat: single?.REPEAT ? true : false,
                    isResolved: single?.solutionStatus === statusId,
                    priority: getPriority(parseInt(single.priority)),
                    userName: userName || '',
                }
                : null;

            if (!result) return;
            setSelectedDataRow(single);
            setSelectedItem(result);
            setSelectedKeys(new Set([result.id]));
            return result;
        } catch (err) {
            console.error('getLayout error:', err);
        } finally {
            setShowLoaderDetail(false);
        }
    };

    const reloadTasks = useCallback((filterParams: any) => {
        setShowCards(true);
        setStopLoadData(false);
        queryParams.current = filterParams;
        totalProductListRec.current = 0;
        offSetLimit.current = 0;
        listTaskList.reload();
    }, []);

    const handleTableRowClick = (rowId: any): void => {
        let selectedRow = rowsData.find((item: any) => item.id === rowId);
        if (selectedKeys?.has(rowId.currentKey) || rowId === null) {
            return;
        } else {
            setShowLoaderDetail(true);
            setSelectedKeys(new Set([rowId]));
            // setShowCards(false);
            setPageView('Full');
            dataRef.current.pageView = 'Full';
            if (!selectedRow) {
                setSelectedDataRow({});
                setSelectedKeys(undefined);
                setDefinition(null);
                return;
            }
            getLayout(selectedRow.id, undefined);
        }
    };

    const handleListRowClick = (rowId: any): void => {
        let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
        if (selectedKeys?.has(selectedRow.id) || rowId === null) {
            return;
        } else {
            setShowLoaderDetail(true);
            setSelectedKeys(rowId);
            setPageView('Full');
            dataRef.current.pageView = 'Full';
            if (!selectedRow) {
                setSelectedDataRow({});
                setSelectedKeys(undefined);
                setDefinition(null);
                return;
            }
            getLayout(selectedRow.id);
        }
    };

    const handleRowClickMobile = (rowId: any): void => {
        const nameSearchExists = queryParams.current.FILTER.COL.find((col: any) => col.name === 'contains');
        if (nameSearchExists) {
            setSearchedValue(nameSearchExists.value);
        }
        setSelectedKeys(undefined);
        let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
        if (!selectedRow) {
            setSelectedDataRow({});
            setSelectedKeys(undefined);
            setDefinition(null);
            return;
        }
        getLayout(selectedRow.id);
        setShowMobileDetailView(true);
    };

    const handleViewChange = (view: string) => {
        setPageView(view);
        setShowCards(true);
        dataRef.current.pageView = view;
    };

    const fmtDate = (dt?: string | null) =>
        dt ? moment(dt).format('MM/DD/YYYY') : undefined;
    const fmtTime = (dt?: string | null) =>
        dt ? moment(dt).format('HH:mm') : undefined;

    type YesNo = '0' | '1';
    type User = { id: string; };

    function normalizeEntries(payload: any): any[] {
        if (!payload) return [];
        if (Array.isArray(payload)) return payload;

        if (typeof payload === 'object') {
            const keys = Object.keys(payload);
            const allNumeric = keys.length > 0 && keys.every(k => /^\d+$/.test(k));
            if (allNumeric) {
                return keys.sort((a, b) => Number(a) - Number(b)).map(k => payload[k]);
            }
            return [payload];
        }
        return [];
    }

    const Loader = () => (
        <Flex width="100%" justifyContent="center" marginTop={10}>
            <ProgressCircle aria-label="Loading…" isIndeterminate />
        </Flex>
    );

    const NoResultsMessage = () => (
        <Flex direction="row" width="100%" alignContent="center" justifyContent="center">
            <IllustratedMessage>
                <NotFound />
                <Heading>{t('no_results', { ns: 'layout_components' })}</Heading>
                <Content>{t('try_another_search', { ns: 'layout_components' })}</Content>
            </IllustratedMessage>
        </Flex>
    );

    const getHeight = (baseHeight: number) => {
        if (browserName === 'Chrome') {
            return `${baseHeight - 120}px`;
        } else if (browserName === 'Safari') {
            return `${baseHeight - 120}px`;
        } else {
            return `${baseHeight - 120}px`;
        }
    };

    const getWidth = () => {
        return `95%`;
    };

    const detailStyle = {
        height: getHeight(screenSize.height - 20),
    };

    const cardStyle = {
        height: 'auto',
        width: getWidth(),
        top: 0,
    };

    const filterStyle = {
        height: getHeight(screenSize.height + 60),
        overflow: 'hidden auto',
    };

    const handleAddNew = () => {
        setShowAdd(true);
    };

    const handleAddNewClose = () => {
        setShowAdd(false);
        setIsEdit(false);
    };

    const handleEditClick = (data: boolean) => {
        if (data) {
            setIsEdit(data);
            setShowAdd(true);
        }
    };

    const refreshDetailView = async (task: any) => {
        setShowLoaderDetail(true);
        setSelectedKeys(new Set([task.ident]));
        const p = await getLayout(task.ident) as Character;
        const index = listTaskList.items.findIndex((row: any) => row.id === task.ident);
        if (index !== -1) {
            listTaskList.items[index] = { ...listTaskList.items, ...p };
        }
    };

    const handleRefreshAddNew = async (task: any) => {
        setShowLoaderDetail(true);
        setSelectedKeys(new Set([task.ident]));
        const taskData = await getLayout(task.ident) as Character;
        const data = [...rowsData];
        setRowsData([...data, taskData]);
        listTaskList.prepend(taskData);
    };

    const handleTaskDelete = async (data: any) => {
        const taskDelete = await schedulerService.deleteTask(new DeleteTaskRequest(store.Server, store.SessionId, data.id));
        if (taskDelete.result === 'OK') {
            setOpen(false);
            ToastQueue.positive(_m('task_deleted'), { timeout: 3000 });
            setRowsData(rowsData.filter((item: any) => item.id !== data.id));
            setSelectedItem(rowsData[0] || null);
            setSelectedDataRow(rowsData[0] || {});
            setSelectedKeys(new Set([rowsData[0]?.id]));
            listTaskList.reload();
        } else {
            ToastQueue.negative(taskDelete.EXCEPTION.message, { timeout: 3000 });
        }
    };

    function getClosedStatusIdent(taskStatusList: any[]) {
        const candidates = (taskStatusList || [])
            .filter(s => s?.ident && typeof s.ident === "string")
            .filter(s => s.ident !== "All states")      // ignore fake row
            .filter(s => (s.title || "").trim() !== ""); // ignore blanks

        const closed = candidates.find(s => CLOSED_TITLES.has(normalizeText(s.title)));
        console.log('close status', closed)
        return closed;
    }

    const updateTaskStatus = async (isSelected: boolean, ident: string, isMulti: boolean) => {
        if (!isSelected) return;
        const statusItem = getClosedStatusIdent(taskStatusList);
        // const statusItem = taskStatusList.find((item: any) => (item.title === 'Uzavřen' || item.title === 'Closed'));
        const statusId = statusItem ? statusItem.ident : '';
        console.log('close status Id', statusId)

        const payLoad: TaskItem = {
            ...selectedDataRow,
            completed: new Date().toISOString(),
            solutionStatus: statusId,
            headUserId: store.UserId.toString(),
        }
        setShowLoaderDetail(true);
        try {
            const response = await schedulerService.updateTask(new UpdateTaskRequest(store.Server, store.SessionId, payLoad));
            if (response.result === 'OK') {
                setShowLoaderDetail(false);
                setSelectedItem(null);
                setSelectedDataRow({});
                setSelectedKeys(undefined);
                getLayout(ident);
                ToastQueue.positive(_m('task_closed'), { timeout: 3000 });
            } else {
                setShowLoaderDetail(false);
                ToastQueue.negative(response.EXCEPTION.message || 'Failed to update task status', { timeout: 3000 });
            }
        } catch (error) {
            console.error('Error updating task status:', error);
        }

    };

    const updateTaskDescription = async (description: string) => {
        setShowUpdateDescLoader(true);
        const payLoad: TaskItem = {
            ...selectedDataRow,
            comments: toBase64Unicode(description),
            headUserId: store.UserId.toString(),
        }
        try {
            const response = await schedulerService.updateTask(new UpdateTaskRequest(store.Server, store.SessionId, payLoad));
            if (response.result === 'OK') {
                setShowUpdateDescLoader(false);
                ToastQueue.positive(_m('task_description_updated'), { timeout: 3000 });
            } else {
                ToastQueue.negative(response.EXCEPTION.message || 'Failed to update task status', { timeout: 3000 });
                setShowUpdateDescLoader(false);
            }
        } catch (error) {
            setShowUpdateDescLoader(false);
            console.error('Error updating task status:', error);
        }
    };

    useKeyboardShortcuts([...addKey('c', { ctrl: true, shift: true }, () => handleAddNew())], true);

    const shouldUseDesktopLayout = !deviceInfo.shouldUseMobileLayout() || ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape');

    const COLUMN_WIDTHS: Record<string, string> = {
        title: '30%',
        priority: '10%',
    };

    return (
        <>
            {!shouldUseDesktopLayout ? (
                <>
                    {!showMobileDetailView && showCards && (
                        <>
                            <FilterComponent onFilterChange={reloadTasks} filterParams={queryParams.current} />
                            <View position={'relative'} width={'100%'} height={'100%'} UNSAFE_className={styles.card_list_parent}>
                                <Flex direction={'column'} position={'absolute'} width={'100%'} top={'5px'} left={'0%'} alignItems={'start'} justifyContent={'start'} gap={'12px'} maxHeight={'100vh'}>
                                    {showLoader ? (
                                        <Loader />
                                    ) : (
                                        <ListBox
                                            autoFocus
                                            id="eventCards"
                                            selectionMode="single"
                                            onSelectionChange={(rowId) => handleRowClickMobile(rowId)}
                                            marginStart={'15px'}
                                            minHeight={'130px'}
                                            aria-label="Task Cards"
                                            items={listTaskList.items}
                                            isLoading={listTaskList.isLoading}
                                            // onLoadMore={listTaskList.items.length > 45 ? listTaskList.loadMore : undefined}
                                            UNSAFE_style={{ width: '100%' }}
                                        >
                                            {(item: any) => (
                                                <Item key={item.id} textValue={item.title}>
                                                    <CardComponentMobile item={item} />
                                                </Item>
                                            )}
                                        </ListBox>
                                    )}
                                </Flex>
                                <Flex
                                    direction={'row'}
                                    position={'fixed'}
                                    right={'16px'}
                                    bottom={'95px'}
                                    height={'48px'}
                                    justifyContent={'center'}
                                    alignItems={'center'}
                                    gap={'8px'}
                                    UNSAFE_className={styles.add_new_parent}
                                >
                                    <Link onPress={handleAddNew} isQuiet aria-label="Actions" UNSAFE_className={styles.add_new_btn}>
                                        <i className="bi bi-plus fs-5">
                                            <View UNSAFE_className={styles.add_new_btn_text}>{_tt('button_add_new')}</View>
                                        </i>
                                    </Link>
                                </Flex>
                            </View>
                        </>
                    )}

                    {(showMobileDetailView || !showCards) && (
                        <>
                            <View id="top" position={'relative'} width={'100%'} height={'64px'} overflow={'hidden'} UNSAFE_className={styles.layout_header_parent}>
                                <Flex gap={'size-150'} position={'relative'} top={'calc(50% - 15px)'} left={'15px'} height={'30px'} UNSAFE_className={styles.layout_header_btn_back}>
                                    <Link
                                        onPress={() => {
                                            setStopLoadData(true);
                                            setShowMobileDetailView(false);
                                            setShowCards(true);
                                        }}
                                    >
                                        <i className="bi bi-chevron-left fs-5"></i>
                                    </Link>
                                    <span>{_t("task_details")}</span>
                                </Flex>
                            </View>
                            <Flex direction="column" alignItems={'start'} justifyContent={'center'} UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ padding: '0px', paddingTop: '0px' }}>
                                {selectedItem && (
                                    <>
                                        {showLoader ? (
                                            <Loader />
                                        ) : (
                                            <>
                                                <View UNSAFE_className={styles.service_status_bar} marginBottom={'5px'} marginTop={'0px'} UNSAFE_style={{ backgroundColor: selectedItem.statusColor }}></View>
                                                <TaskDetailsViewMobile
                                                    task={selectedItem}
                                                    taskStatusList={taskStatusList}
                                                    updateTaskStatus={updateTaskStatus}
                                                    onEdit={handleEditClick}
                                                    onDelete={() => setOpen(true)}
                                                    updateTaskDescription={updateTaskDescription}
                                                    showUpdateDescLoader={showUpdateDescLoader}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </Flex>
                        </>
                    )}
                </>
            ) :
                (showCards ? (
                    <Grid
                        areas={
                            (deviceInfo.isTablet || deviceInfo.isIPad)
                                ? pageView === 'Full'
                                    ? ['header header', 'filter filter', 'card detail']
                                    : ['header header', 'filter filter', 'detail detail']
                                : pageView === 'Full'
                                    ? ['filter header header', 'filter card detail']
                                    : ['filter header header', 'filter detail detail']
                        }
                        columns={{
                            base: ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape')
                                ? ['35%', '65%']
                                : ['245px', '28%', 'auto'],
                            L: ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape')
                                ? ['35%', '65%']
                                : ['245px', '28%', 'auto']
                        }}
                        rows={
                            (deviceInfo.isTablet || deviceInfo.isIPad)
                                ? ['auto', 'auto', '1fr']
                                : ['auto', '1fr']
                        }
                        height="100%"
                        width="100%"
                        UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '0px', margin: '0px', overflow: 'hidden' }}
                    >
                        {!(deviceInfo.isTablet || deviceInfo.isIPad) && (
                            <View gridArea="filter">
                                {showFilter ? (
                                    <FilterComponent onFilterChange={reloadTasks} filterParams={queryParams.current} filterStyle={filterStyle} />
                                ) : (
                                    <Flex
                                        height={'100%'}
                                        justifyContent={'center'}
                                        alignItems={'center'}
                                        UNSAFE_style={{ backgroundColor: 'white' }}
                                    >
                                        <Loader />
                                    </Flex>
                                )}
                            </View>
                        )}
                        <View width={'100%'} gridArea="header" UNSAFE_style={pageView == 'Full' ? {} : { height: '100%' }}>
                            <HeaderComponent
                                onPageViewChange={handleViewChange}
                                autoChanged={pageView}
                                filterParams={queryParams.current}
                                onSearchChange={reloadTasks}
                                fpaTypeCategory={serviceActivityValue.current}
                                onAddNew={handleAddNew}
                            />
                        </View>
                        {(deviceInfo.isTablet || deviceInfo.isIPad) && (
                            <View gridArea="filter" UNSAFE_style={{ height: '60px', backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
                                <FilterComponent onFilterChange={reloadTasks} filterParams={queryParams.current} filterStyle={filterStyle} />
                            </View>
                        )}
                        {pageView == 'Full' ? (
                            <>
                                {showLoader ? (
                                    <Loader />
                                ) : (
                                    <>
                                        <View gridArea="card" UNSAFE_style={{ backgroundColor: '#f5f6f9', overflow: 'auto' }}>
                                            {rowsData?.length > 0 ? (
                                                <ListBox
                                                    autoFocus
                                                    id="eventCards"
                                                    selectionMode="single"
                                                    onSelectionChange={(rowId) => handleListRowClick(rowId)}
                                                    marginStart={'15px'}
                                                    defaultSelectedKeys={selectedKeys}
                                                    selectedKeys={selectedKeys}
                                                    aria-label="Task Cards"
                                                    items={listTaskList.items}
                                                    // onLoadMore={listTaskList.loadMore}
                                                    isLoading={listTaskList.isLoading}
                                                    UNSAFE_style={{ ...cardStyle, marginTop: 13 }}
                                                >
                                                    {(item: any) => (
                                                        <Item key={item.id} textValue={item.title}>
                                                            <CardComponent item={item} />
                                                        </Item>
                                                    )}
                                                </ListBox>
                                            ) : (
                                                <View marginTop={'13px'}>
                                                    <NoResultsMessage />
                                                </View>
                                            )}
                                        </View>
                                        <View
                                            gridArea="detail"
                                            backgroundColor={'static-white'}
                                            borderRadius={'large'}
                                            padding={'size-100'}
                                            paddingTop={'0'}
                                            marginTop={'17px'}
                                            UNSAFE_style={detailStyle}
                                            width={'100%'}
                                            UNSAFE_className={styles.detailView}
                                        >
                                            {showLoaderDetail ? (
                                                <Loader />
                                            ) : (
                                                <View height="100%" UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ overflow: 'hidden auto' }} marginStart={'10px'}>
                                                    {selectedItem && (
                                                        <>
                                                            {rowsData?.length > 0 ? (
                                                                <>
                                                                    <View UNSAFE_className={styles.service_status_bar} UNSAFE_style={{ backgroundColor: selectedItem.statusColor }}></View>
                                                                    <View height={'2px'}>&nbsp;</View>
                                                                    <TaskDetailsView
                                                                        task={selectedItem}
                                                                        statusList={taskStatusList}
                                                                        updateTaskStatus={updateTaskStatus}
                                                                        updateTaskDescription={updateTaskDescription}
                                                                        showUpdateDescLoader={showUpdateDescLoader}
                                                                        onEdit={handleEditClick}
                                                                        onDelete={() => setOpen(true)}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <View marginTop={'13px'}>
                                                                    <NoResultsMessage />
                                                                </View>
                                                            )}
                                                        </>
                                                    )}
                                                </View>
                                            )}
                                        </View>
                                    </>
                                )}
                            </>
                        ) : (
                            <View gridArea="detail">
                                {showLoader ? (
                                    <Loader />
                                ) : (
                                    <View position={'relative'} overflow={'auto'} height={'100%'} width={'100%'} UNSAFE_className={styles.service_activities}>
                                        {columnsData?.length > 0 && rowsData?.length > 0 ? (
                                            <TableView
                                                UNSAFE_className="tblLayoutComponent"
                                                aria-label="Tasks"
                                                selectionMode="single"
                                                defaultSelectedKeys={selectedKeys}
                                                selectionStyle="highlight"
                                                width={{ base: '100%', L: '100%', M: '1100px' }}
                                                minHeight={'100px'}
                                                maxHeight={{ base: '100vh', L: '625px' }}
                                                onAction={handleTableRowClick}
                                            >
                                                <TableHeader columns={columnsData}>
                                                    {(column: any) => (
                                                        <Column
                                                            showDivider
                                                            key={column.key}
                                                            width={COLUMN_WIDTHS[column.key] as any}
                                                        >
                                                            {column.name}
                                                        </Column>
                                                    )}
                                                </TableHeader>
                                                <TableBody
                                                    onLoadMore={listTaskList.items.length > 40 ? listTaskList.loadMore : undefined}
                                                    items={listTaskList.items}
                                                    loadingState={listTaskList.loadingState}>
                                                    {(item: any) => (
                                                        <Row>
                                                            {columnKey => (
                                                                <Cell>
                                                                    {(() => {
                                                                        return item[columnKey];
                                                                    })()}
                                                                </Cell>
                                                            )}
                                                        </Row>
                                                    )}
                                                </TableBody>
                                            </TableView>
                                        ) : (
                                            <NoResultsMessage />
                                        )}
                                    </View>
                                )}
                            </View>
                        )}
                    </Grid>
                ) :
                    <Grid
                        areas={pageView == 'Full' ? ['detail'] : ['detail']}
                        columns={{ base: ['100%'], L: ['100%'] }}
                        rows={['auto', '1fr']}
                        height="100%"
                        width="100%"
                        UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '10px', margin: '0px', overflow: 'hidden' }}
                    >
                        {pageView == 'Full' ? (
                            <>
                                {showLoader ? (
                                    <Loader />
                                ) : (
                                    <>
                                        <View
                                            gridArea="detail"
                                            backgroundColor={'static-white'}
                                            borderRadius={'large'}
                                            padding={'size-200'}
                                            UNSAFE_style={detailStyle}
                                            width={'100%'}
                                            UNSAFE_className={styles.detailView}
                                        >
                                            {showLoaderDetail ? (
                                                <Loader />
                                            ) : (
                                                <View height="100%" UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ overflow: 'hidden auto' }} marginStart={'10px'}>
                                                    {selectedItem && (
                                                        <>
                                                            {rowsData?.length > 0 ? (
                                                                <>
                                                                    <View UNSAFE_className={styles.service_status_bar} UNSAFE_style={{ backgroundColor: selectedItem.statusColor }}></View>
                                                                    <View height={'2px'}>&nbsp;</View>
                                                                    <TaskDetailsView
                                                                        task={selectedItem}
                                                                        showUpdateDescLoader={showUpdateDescLoader}
                                                                        statusList={taskStatusList}
                                                                        updateTaskStatus={updateTaskStatus}
                                                                        updateTaskDescription={updateTaskDescription}
                                                                        onEdit={handleEditClick}
                                                                        onDelete={() => setOpen(true)}
                                                                        handleBackClick={() => setShowCards(true)}
                                                                    />
                                                                </>
                                                            ) : (
                                                                ''
                                                            )}
                                                        </>
                                                    )}
                                                </View>
                                            )}
                                        </View>
                                    </>
                                )}
                            </>
                        ) : (
                            <View gridArea="detail">
                                {showLoader ? (
                                    <Loader />
                                ) : (
                                    <View position={'relative'} overflow={'auto'} height={'100%'} width={'100%'} UNSAFE_className={styles.service_activities}>
                                        {columnsData?.length > 0 && rowsData?.length > 0 ? (
                                            <TableView
                                                UNSAFE_className="tblLayoutComponent"
                                                aria-label="Tasks"
                                                selectionMode="single"
                                                defaultSelectedKeys={selectedKeys}
                                                selectionStyle="highlight"
                                                width={{ base: '100%', L: '100%', M: '1100px' }}
                                                minHeight={'100px'}
                                                maxHeight={{ base: '100vh', L: '625px' }}
                                                onAction={handleTableRowClick}
                                            >
                                                <TableHeader columns={columnsData}>
                                                    {(column: any) => (
                                                        <Column showDivider key={column.key}>
                                                            {column.name}
                                                        </Column>
                                                    )}
                                                </TableHeader>
                                                <TableBody onLoadMore={listTaskList.items.length > 40 ? listTaskList.loadMore : undefined} items={listTaskList.items} loadingState={listTaskList.loadingState}>
                                                    {(item: any) => (
                                                        <Row>
                                                            {columnKey => (
                                                                <Cell>
                                                                    {(() => {
                                                                        return item[columnKey];
                                                                    })()}
                                                                </Cell>
                                                            )}
                                                        </Row>
                                                    )}
                                                </TableBody>
                                            </TableView>
                                        ) : (
                                            <NoResultsMessage />
                                        )}
                                    </View>
                                )}
                            </View>
                        )}
                    </Grid>
                )
            }
            <DialogContainer onDismiss={handleAddNewClose}>
                {showAdd && <AddUpdateTask
                    task={selectedDataRow}
                    isEdit={isEdit}
                    onClose={handleAddNewClose}
                    refreshDetailView={refreshDetailView}
                    handleRefreshAddNew={handleRefreshAddNew}
                    taskStatusList={taskStatusList}
                    typeOfWorkList={typeofWorkList}
                    userList={userData} />
                }
            </DialogContainer>
            <DeleteConfirmationDialog
                isOpen={isOpen}
                onClose={() => setOpen(false)}
                onConfirm={() => handleTaskDelete(selectedItem)}
                itemName={selectedItem?.name}
            />
        </>
    );
}